import { makeAutoObservable, computed, runInAction } from 'mobx';
import type { Contact } from './types';
import type { RootStore } from './RootStore';

export class ContactStore {
  contacts = new Map<string, Contact>();
  private root: RootStore;

  // Secondary index: companyId → Set of contact IDs (plain, not observable)
  private _companyIdIndex = new Map<string, Set<string>>();

  constructor(root: RootStore) {
    this.root = root;
    makeAutoObservable(this, {
      all: computed,
      companies: computed,
      people: computed,
    });
  }

  // ── Index helpers ──────────────────────────────────────────────────────────

  private _addToIndex(key: string | null | undefined, id: string): void {
    if (!key) return;
    if (!this._companyIdIndex.has(key)) this._companyIdIndex.set(key, new Set());
    this._companyIdIndex.get(key)!.add(id);
  }

  private _removeFromIndex(key: string | null | undefined, id: string): void {
    if (!key) return;
    this._companyIdIndex.get(key)?.delete(id);
  }

  private _set(id: string, contact: Contact): void {
    const existing = this.contacts.get(id);
    if (existing?.companyId !== contact.companyId) {
      this._removeFromIndex(existing?.companyId, id);
      this._addToIndex(contact.companyId, id);
    } else if (!existing) {
      this._addToIndex(contact.companyId, id);
    }
    this.contacts.set(id, contact);
  }

  private _delete(id: string): void {
    const existing = this.contacts.get(id);
    if (existing) this._removeFromIndex(existing.companyId, id);
    this.contacts.delete(id);
  }

  // ── Computed ───────────────────────────────────────────────────────────────

  get all(): Contact[] {
    const result: Contact[] = [];
    for (const c of this.contacts.values()) {
      if (!c.isDeleted) result.push(c);
    }
    return result;
  }

  get companies(): Contact[] {
    return this.all.filter(c => c.type === 0);
  }

  get people(): Contact[] {
    return this.all.filter(c => c.type === 1);
  }

  // ── Queries ────────────────────────────────────────────────────────────────

  getById(id: string): Contact | undefined {
    const contact = this.contacts.get(id);
    return contact && !contact.isDeleted ? contact : undefined;
  }

  getByCompanyId(companyId: string): Contact[] {
    const ids = this._companyIdIndex.get(companyId);
    if (!ids || ids.size === 0) return [];
    const result: Contact[] = [];
    for (const id of ids) {
      const c = this.contacts.get(id);
      if (c && !c.isDeleted) result.push(c);
    }
    return result;
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  loadFromRecords(records: Record<string, unknown>[]): void {
    runInAction(() => {
      for (const record of records) {
        this._set(record.id as string, record as unknown as Contact);
      }
    });
  }

  // ── Sync ───────────────────────────────────────────────────────────────────

  applyChanges(changes: { id: string; data?: Record<string, unknown>; isDeleted: boolean }[]): void {
    runInAction(() => {
      for (const change of changes) {
        if (change.isDeleted) {
          this._delete(change.id);
        } else if (change.data) {
          this._set(change.id, change.data as unknown as Contact);
        }
      }
    });
  }

  removeFromMap(id: string): void {
    this._delete(id);
  }

  // ── Mutations ──────────────────────────────────────────────────────────────

  create(data: Partial<Contact>): Contact {
    const contact: Contact = {
      id: crypto.randomUUID(),
      type: data.type ?? 1,
      name: data.name ?? '',
      // The active-email unique index ignores NULL but not "". A contact
      // created without an email must not block the next one.
      email: data.email?.trim() || undefined,
      phone: data.phone,
      address: data.address,
      notes: data.notes,
      companyId: data.companyId,
      ownerId: data.ownerId,
      orgId: '',
      syncVersion: 0,
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this._set(contact.id, contact);
    this.root.syncStore.queueMutation('contact', 'create', contact.id, contact);
    return contact;
  }

  update(id: string, patch: Partial<Contact>): void {
    const existing = this.contacts.get(id);
    if (!existing) return;
    const updated = { ...existing, ...patch, updatedAt: new Date().toISOString() };
    this._set(id, updated);
    this.root.syncStore.queueMutation('contact', 'update', id, patch);
  }

  delete(id: string): void {
    const existing = this.contacts.get(id);
    if (existing) {
      this._set(id, { ...existing, isDeleted: true, deletedAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
    }
    this.root.syncStore.queueMutation('contact', 'delete', id);
  }

  restore(id: string): void {
    const existing = this.contacts.get(id);
    if (existing) {
      this._set(id, { ...existing, isDeleted: false, deletedAt: undefined, updatedAt: new Date().toISOString() });
      this.root.syncStore.queueMutation('contact', 'update', id, { isDeleted: false });
    }
  }

  clear(): void {
    this.contacts.clear();
    this._companyIdIndex.clear();
  }
}
